import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  ADEQUATIONS,
  CRITERES_CV,
  CRITERES_VIDES,
  LIBELLES_ADEQUATION,
  LIBELLES_CRITERE_CV,
  VILLE_NON_RENSEIGNEE,
  critereActif,
  villesDe,
  type CandidatureResume,
  type CriteresListe as Criteres,
} from '@rh/shared';
import { libelleSource } from '../format';

function basculer<T>(liste: readonly T[], valeur: T): T[] {
  return liste.includes(valeur) ? liste.filter((v) => v !== valeur) : [...liste, valeur];
}

function compter<T>(liste: readonly CandidatureResume[], cle: (c: CandidatureResume) => T): Map<T, number> {
  const comptes = new Map<T, number>();
  for (const c of liste) comptes.set(cle(c), (comptes.get(cle(c)) ?? 0) + 1);
  return comptes;
}

/** Nombre de critères posés, tel que l'affiche la pastille du bouton. */
function nombreCriteres(criteres: Criteres): number {
  return (
    criteres.sources.length +
    Number(criteres.cv !== 'tous') +
    criteres.villes.length +
    criteres.adequations.length +
    Number(criteres.pastilleMin !== null) +
    Number(criteres.whatsapp)
  );
}

/**
 * Critères combinables de la liste par annonce : site d'annonce, CV joint,
 * ville, adéquation, pastille minimale. Les choix proposés sont ceux présents
 * dans la liste, avec leur nombre sur la liste entière. Jamais d'âge ni de
 * sexe : l'application ne les stocke pas (CLAUDE.md § Interdits).
 *
 * Depuis le 2026-09-19 (demande d'ergonomie) ils ne tiennent plus la page :
 * le bouton « Filtres » ouvre une fenêtre, les critères posés s'y appliquent
 * en direct, et « Voir les N profils » la referme. Ce qui reste visible sur la
 * liste, ce sont les étiquettes des critères actifs, chacune retirable d'un
 * clic.
 */
export function CriteresListe({
  liste,
  criteres,
  nombreRetenu,
  onChange,
}: {
  liste: readonly CandidatureResume[];
  criteres: Criteres;
  /** Profils retenus par les critères ET le filtre rapide, pour le bouton de fermeture. */
  nombreRetenu: number;
  onChange: (criteres: Criteres) => void;
}) {
  const sources = useMemo(() => compter(liste, (c) => c.source), [liste]);
  const villes = useMemo(() => villesDe(liste), [liste]);
  const adequations = useMemo(() => compter(liste, (c) => c.adequation), [liste]);
  const avecCv = liste.filter((c) => c.cvJoint).length;
  const actifs = nombreCriteres(criteres);
  const [ouvert, setOuvert] = useState(false);
  const bouton = useRef<HTMLButtonElement>(null);
  const premier = useRef<HTMLButtonElement>(null);
  const titre = useId();
  const maj = (partiel: Partial<Criteres>) => onChange({ ...criteres, ...partiel });

  function fermer(): void {
    setOuvert(false);
    bouton.current?.focus();
  }

  // Échap ferme la fenêtre, comme le menu principal ; le focus revient au bouton.
  useEffect(() => {
    if (!ouvert) return;
    premier.current?.focus();
    const clavier = (evenement: KeyboardEvent) => {
      if (evenement.key !== 'Escape') return;
      setOuvert(false);
      bouton.current?.focus();
    };
    document.addEventListener('keydown', clavier);
    return () => document.removeEventListener('keydown', clavier);
  }, [ouvert]);

  const puce = (cle: string, libelle: string, nombre: number, choisi: boolean, action: () => void) => (
    <button key={cle} type="button" className={choisi ? 'actif' : ''} aria-pressed={choisi} onClick={action}>
      {libelle} ({nombre})
    </button>
  );

  /** Étiquettes des critères posés, sous le bouton ; chacune se retire d'un clic. */
  const etiquettes: Array<{ cle: string; libelle: string; retirer: () => void }> = [
    ...criteres.sources.map((s) => ({
      cle: `site-${s}`,
      libelle: libelleSource(s),
      retirer: () => maj({ sources: criteres.sources.filter((autre) => autre !== s) }),
    })),
    ...(criteres.cv !== 'tous'
      ? [{ cle: 'cv', libelle: LIBELLES_CRITERE_CV[criteres.cv], retirer: () => maj({ cv: 'tous' as const }) }]
      : []),
    ...criteres.villes.map((v) => ({
      cle: `ville-${v}`,
      libelle:
        villes.find((choix) => choix.cle === v)?.libelle ??
        (v === VILLE_NON_RENSEIGNEE ? 'Non renseignée' : v),
      retirer: () => maj({ villes: criteres.villes.filter((autre) => autre !== v) }),
    })),
    ...criteres.adequations.map((a) => ({
      cle: `adequation-${a}`,
      libelle: LIBELLES_ADEQUATION[a],
      retirer: () => maj({ adequations: criteres.adequations.filter((autre) => autre !== a) }),
    })),
    ...(criteres.whatsapp
      ? [
          {
            cle: 'whatsapp',
            libelle: 'Joignable sur WhatsApp',
            retirer: () => maj({ whatsapp: false }),
          },
        ]
      : []),
    ...(criteres.pastilleMin !== null
      ? [
          {
            cle: 'pastille',
            libelle: `Pastille ${criteres.pastilleMin} ou plus`,
            retirer: () => maj({ pastilleMin: null }),
          },
        ]
      : []),
  ];

  return (
    <div className="barre-filtres">
      <button
        ref={bouton}
        type="button"
        className={`bouton-filtres${actifs > 0 ? ' actif' : ''}`}
        aria-expanded={ouvert}
        aria-haspopup="dialog"
        onClick={() => setOuvert(true)}
      >
        <span className="icone-filtres" aria-hidden="true" />
        Filtres
        {actifs > 0 && <span className="compte-criteres">{actifs}</span>}
      </button>
      {etiquettes.length > 0 && (
        // Une liste de boutons, pas un <ul> : les critères ne sont pas des
        // éléments de contenu, et la liste des candidats reste la seule de l'écran.
        <div className="etiquettes-criteres" role="group" aria-label="Critères posés">
          {etiquettes.map((etiquette) => (
            <button
              key={etiquette.cle}
              type="button"
              onClick={etiquette.retirer}
              aria-label={`Retirer le critère ${etiquette.libelle}`}
            >
              {etiquette.libelle}
              <span aria-hidden="true">✕</span>
            </button>
          ))}
          <button type="button" className="lien" onClick={() => onChange(CRITERES_VIDES)}>
            Tout effacer
          </button>
        </div>
      )}

      {ouvert && (
        <div className="dialogue-fond" onPointerDown={(e) => e.target === e.currentTarget && fermer()}>
          <dialog open className="dialogue dialogue-filtres" aria-modal="true" aria-labelledby={titre}>
            <div className="entete-dialogue">
              <h2 id={titre}>Filtrer les profils</h2>
              <button
                ref={premier}
                type="button"
                className="fermer-dialogue"
                aria-label="Fermer les filtres"
                onClick={fermer}
              >
                ✕
              </button>
            </div>

            <fieldset>
              <legend>Site d’annonce</legend>
              <div className="filtres">
                {[...sources.keys()]
                  .sort((a, b) => libelleSource(a).localeCompare(libelleSource(b), 'fr'))
                  .map((s) =>
                    puce(s, libelleSource(s), sources.get(s)!, criteres.sources.includes(s), () =>
                      maj({ sources: basculer(criteres.sources, s) }),
                    ),
                  )}
              </div>
            </fieldset>

            <fieldset>
              <legend>CV</legend>
              <div className="filtres">
                {CRITERES_CV.map((cv) =>
                  puce(
                    cv,
                    LIBELLES_CRITERE_CV[cv],
                    cv === 'tous' ? liste.length : cv === 'avec' ? avecCv : liste.length - avecCv,
                    criteres.cv === cv,
                    () => maj({ cv }),
                  ),
                )}
              </div>
            </fieldset>

            <fieldset>
              <legend>Ville</legend>
              <div className="filtres filtres-defilants">
                {villes.map((v) =>
                  puce(v.cle, v.libelle, v.nombre, criteres.villes.includes(v.cle), () =>
                    maj({ villes: basculer(criteres.villes, v.cle) }),
                  ),
                )}
              </div>
            </fieldset>

            <fieldset>
              <legend>Adéquation au poste</legend>
              <div className="filtres">
                {ADEQUATIONS.filter((a) => adequations.has(a)).map((a) =>
                  puce(a, LIBELLES_ADEQUATION[a], adequations.get(a)!, criteres.adequations.includes(a), () =>
                    maj({ adequations: basculer(criteres.adequations, a) }),
                  ),
                )}
              </div>
            </fieldset>

            {/* Un booléen, jamais le numéro : un lecteur peut filtrer
                là-dessus sans qu'aucune coordonnée ne lui soit servie. */}
            <label className="case case-whatsapp">
              <input
                type="checkbox"
                checked={criteres.whatsapp}
                onChange={(e) => maj({ whatsapp: e.target.checked })}
              />
              <span>Joignable sur WhatsApp</span>
            </label>

            <label className="tri-liste">
              <span>Pastille minimale</span>
              <select
                value={criteres.pastilleMin ?? ''}
                onChange={(e) => maj({ pastilleMin: e.target.value === '' ? null : Number(e.target.value) })}
              >
                <option value="">Toutes, sans pastille comprise</option>
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n === 5 ? '5 (étoilés)' : `${n} ou plus`}
                  </option>
                ))}
              </select>
            </label>

            <div className="actions">
              {critereActif(criteres) && (
                <button type="button" className="bouton-second" onClick={() => onChange(CRITERES_VIDES)}>
                  Effacer les critères
                </button>
              )}
              <button type="button" className="bouton" onClick={fermer}>
                Voir {nombreRetenu} profil{nombreRetenu > 1 ? 's' : ''}
              </button>
            </div>
          </dialog>
        </div>
      )}
    </div>
  );
}
